import { Button, Carousel, Input, List, Modal, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../../../../../styles/StayUptoDateCSS/StayUpToDateControls.css";
import { updateComponent } from "../../../../../../Utils/SupabaseServices/StayUpToDateDatabase";
import { useLoadTrendingProducts } from "../../../../../../Utils/customHooks/UpToDateContent/useLoadTrendingProducts";
import { getMaxTrendingCount } from "../../../../../../Utils/SupabaseServices/AnalyticsDatabse";
import CustomDateRange from "../../../../../../components/CustomDateRange/CustomDateRange";
import dayjs from "dayjs";
import { AddedTrendingProducts } from "./AddedTrendingProducts";
import { validateTitleForEdit } from "../../../../../../Utils/helpers/carouselHelper";
import CarouselControlWrapper from "../../../../../../components/CarouselControls/CarouselControlWrapper";
import PostCardComponent from "../../../../../../components/EditModalComponents.tsx/PostCardComponent";
import ProductTitle from "../../../../../../components/EditModalComponents.tsx/ProductTitle";
import Skeleton from "react-loading-skeleton";
import { DuplicateNameError } from "../../../../../../Utils/StayUpToDate/RenderComponent";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const { Title, Text } = Typography;

// Add the same styles as TrendingProductsComponent
const sectionStyle = {
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const helperTextStyle = {
  color: '#666',
  fontSize: '14px',
  marginBottom: '16px',
};

// Add this component before the EditTrendingProducts component
export const RemovedTrendingProducts = (props: any) => {
  const {
    username,
    user_id,
    type,
    removePostIds,
    isActionLoading,
    setIsActionLoading,
    handleRemovedPostIds,
    trendingData,
    isLoading,
  } = props;

  const onAdd = async (post_id: string) => {
    setIsActionLoading(true);
    try {
      await handleRemovedPostIds(post_id, "add");
    } finally {
      setIsActionLoading(false);
    }
  };

  const SkeletonStatBox = () => {
    return (
      <div className="skeleton-stat">
        <Skeleton className="skeleton-style" />
      </div>
    );
  };

  if (!isLoading && (!trendingData || trendingData.length === 0)) {
    return null;
  }

  return (
    <>
      <div className="removed-content">
        <label className="bold-label fontsize-12">Removed Content</label>
        <div className="removed-content-container">
          <div className="added-content">
            {isLoading ? (
              <div className="skeleton-stat-box">
                {[...Array(3)].map((_, index) => (
                  <SkeletonStatBox key={index} />
                ))}
              </div>
            ) : (
              trendingData.map((post: any) => (
                <React.Fragment key={post.post_id}>
                  <div className="feed-tab-post-container post-container">
                    <PostCardComponent
                      post={post}
                      onMinus={onAdd}
                      from="removed"
                    />
                    <ProductTitle post={post} type={type} />
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const EditTrendingProducts = (props: any) => {
  const {
    username,
    user_id,
    isTrendingProductsEditModalOpen,
    setIsTrendingProductsEditModalOpen,
    type,
    component,
    refetchDashboard,
    from
  } = props;

  const [name, setName] = useState(component.name || "");
  const [clickNumber, setClickNumber] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [postIds, setPostIds] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [defaultQuery, setDefaultQuery] = useState("");
  const [removePostIds, setRemovePostIds] = useState<any>("");
  const [activeTab, setActiveTab] = useState(2);
  const [localActiveTab, setLocalActiveTab] = useState(activeTab);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<any>("");
  const [customEndDate, setCustomEndDate] = useState<any>("");
  const [days, setDays] = useState<any>("");
  const [isChanged, setIsChanged] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editIndex, setEditIndex] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [editPostIds, setEditPostIds] = useState<any>("")
  const [isEditDate, setIsEditDate] = useState(false);
  const [prevClickNumber, setPrevClickNumber] = useState(clickNumber);
  const [shape, setShape] = useState("rect");
  const [isSoundOn, setIsSoundOn] = useState(false);

  // Single API call for trending data
  const {
    isLoading,
    data: allTrendingData,
    refetch,
  }: any = useLoadTrendingProducts(
    user_id,
    username,
    name,
    clickNumber,
    "", // Don't filter out any posts here - we'll filter client-side
    customStartDate,
    customEndDate,
    days,
    true,
    type || "trending products" // Use the passed type or default to "trending products"
  );

  // Filter the data for added and removed sections
  const trendingData = useMemo(() => {
    if (!allTrendingData) return [];

    const removedIds = editPostIds && editPostIds?.length > 0 && editPostIds.split(',').filter(Boolean);
    return allTrendingData.filter((post: any) => !removedIds.includes(post.post_id));
  }, [allTrendingData, editPostIds]);

  // Data for removed section
  const removedTrendingData = useMemo(() => {
    if (!allTrendingData || !editPostIds) return [];

    const removedIds = editPostIds.split(',').filter(Boolean);
    return allTrendingData.filter((post: any) => removedIds.includes(post.post_id));
  }, [allTrendingData, editPostIds]);

  useEffect(() => {
    setPostIds(component?.post_ids);
    setName(component?.name);
    setDefaultQuery(component?.query);
    setEditPostIds(component?.removed_postids)
    const [numberOfClicks, startDate, endDate, activeKey] = (
      component?.details ?? ""
    ).split(",");
    setDays(activeKey);
    setClickNumber(numberOfClicks);
    if (activeKey === "7" || activeKey === "30" || activeKey === "90") {
      const startDate = dayjs().tz(userTimezone).utc().subtract(activeKey, "day").format("YYYY-MM-DD");
      const endDate = dayjs().tz(userTimezone).utc().format("YYYY-MM-DD");
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    } else {
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }

    setShape(component?.content_shape);
    setIsSoundOn(component?.content_sound);
    setActiveTab(+activeKey); // The unary + operator converts the string to a number
  }, [component]);

  useEffect(() => {
    // Only run fetchMaxCount when clickNumber actually changes from its previous value
    if (clickNumber !== prevClickNumber) {
      setPrevClickNumber(clickNumber);
      fetchMaxCount();
    }
  }, [clickNumber, prevClickNumber]);

  const handleInputChange = (value: string, setter: Function) => {
    const numericValue = parseInt(value, 10);

    // Check for minimum value of 1
    if (numericValue < 1) {
      setErrorMsg("Number should not be less than 1");
      setter(""); // Clear invalid input
    } 
    // else if (numericValue > maxCount) {
    //   setErrorMsg(`Number should not exceed ${maxCount}`);
    // }
     else {
      setErrorMsg("");
      setIsButtonDisabled(!value);
      setter(value);
    }
  };
  const [maxCount, setMaxCount] = useState<any>();
  async function fetchMaxCount() {
    const res = await getMaxTrendingCount(username, user_id);
    setMaxCount(res);
  }
  const handleOk = async () => {
    if (errorMsg) {
      return;
    }

    if (!name.trim()) {
      setErrorMsg("Title is required");
      return;
    }
    // Validate the new title
    const validation = validateTitleForEdit(name, component);
    if (!validation.isValid) {
      setErrorMsg(validation.error || "Invalid title");
      return;
    }

    setRemovePostIds(editPostIds)
    setConfirmLoading(true);

    const res = await updateComponent(
      name,
      component,
      username,
      user_id,
      defaultQuery,
      component?.type,
      "",
      `${clickNumber},${customStartDate},${customEndDate},${activeTab}`,
      editPostIds,
      undefined,
      shape,
      isSoundOn,
      undefined,
      from
      // removePostIds
    );
    if (res === "true") {
      // props.refetch();
      refetchDashboard();
      props.setIsTrendingProductsEditModalOpen(false);
      props.setSelectedComponent("");
      setConfirmLoading(false);
      setIsChanged(true);
      setName("");
      setPostIds("");
      setErrorMsg("");
      setDefaultQuery("");
      setRemovePostIds("");
      setEditPostIds("")
    } else {
      setErrorMsg(DuplicateNameError());
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    props.setIsTrendingProductsEditModalOpen(false);
    props.setSelectedComponent("");
    refetchDashboard();
    setName("");
    setPostIds("");
    setErrorMsg("");
    setDefaultQuery("");
    setRemovePostIds("");
    setEditPostIds("")
  };

  function handleRemovedPostIds(post_ids: any, type: any) {
    if (type === "minus") {
      // setRemovePostIds((prev: any) => {
      //   const idsArray = prev?.split(",").filter(Boolean);
      //   idsArray.push(post_ids);

      //   return idsArray.join(",");
      // });
      setEditPostIds((prev: any) => {
        const idsArray = prev?.split(",").filter(Boolean);
        idsArray.push(post_ids);

        return idsArray.join(",");
      });
    } else if (type === "add") {
      // setRemovePostIds((prev: any) => {
      //   const idsArray = prev.split(",").filter(Boolean);
      //   const filteredArray = idsArray.filter((elem: any) => elem !== post_ids);
      //   return filteredArray.join(",");
      // });
      setEditPostIds((prev: any) => {
        const idsArray = prev?.split(",").filter(Boolean);
        const filteredArray = idsArray.filter((elem: any) => elem !== post_ids);
        return filteredArray.join(",");
      });
    }
  }
  function handleEdit() {
    setIsEditDate(true);
    // Set localActiveTab to current activeTab to preserve the current selection
    setLocalActiveTab(activeTab);

    // Only set selectedDates if we're in custom date mode (activeTab === 1)
    if (activeTab === 1) {
      setSelectedDates([dayjs(customStartDate), dayjs(customEndDate)]);
    }
  }
  function handleUpdateCancel() {
    setIsEditDate(false);
  }
  function handleUpdateOk() {
    let startDate: any;
    let endDate: any;

    if (localActiveTab !== 1 && localActiveTab !== 2) {
      endDate = dayjs().tz(userTimezone).utc().add(1, "day").format("YYYY-MM-DD");
      startDate = dayjs().tz(userTimezone).utc().subtract(localActiveTab, "day").format("YYYY-MM-DD");
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    } else {
      if (localActiveTab === 2) {
        endDate = dayjs().tz(userTimezone).utc().add(1, "day").format("YYYY-MM-DD");
        startDate = dayjs().tz(userTimezone).utc().subtract(100000, "day").format("YYYY-MM-DD");
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
      } else {
        startDate = dayjs(customStartDate)?.tz(userTimezone).utc().format("YYYY-MM-DD");
        endDate = dayjs(customEndDate)?.tz(userTimezone).utc().add(1, "day").format("YYYY-MM-DD");
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
      }
    }
    setActiveTab(localActiveTab);
    setIsEditDate(false);
  }

  const renderDate = useCallback(
    (startDate: string, endDate: string) => {
      if (activeTab != 2) {
        return (
          <p className="m-0">
            {dayjs(startDate).format("MMMM D, YYYY")} -{" "}
            {dayjs(endDate).subtract(1, "day").format("MMMM D, YYYY")}
          </p>
        );
      } else {
        return <p className="active-tab">Showing All data</p>;
      }
    },
    [activeTab] // This ensures the function updates only when activeTab changes
  );

  return (
    <>
      <Modal
        open={isTrendingProductsEditModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Update"
        confirmLoading={confirmLoading}
        okButtonProps={{
          disabled: !name || !clickNumber
        }}
        focusTriggerAfterClose={false}
        getContainer={false}
        className="add-to-page-modal"
        width={600}
        centered
        closeIcon={<span className="modal-close-icon">×</span>}
      >
        <div className="mt-1">
          <Title level={5} style={{ textAlign: 'center', marginBottom: '8px' }}>Edit Trending Products Carousel</Title>
          {/* <Text style={{ display: 'block', textAlign: 'center', color: '#666', marginBottom: '24px' }}>
            Use the options below to filter trending products based on a specific time frame and the number of times the shop button is clicked after which a product is considered trending. This will help you showcase the most relevant products that are gaining popularity
          </Text> */}

          {errorMsg && <Text type="danger" style={{ display: 'block', textAlign: 'center', marginBottom: '16px' }}>{errorMsg}</Text>}

          {/* Date Range Section */}
          {/* <div style={sectionStyle}>
            <Title level={5} style={{ margin: 0 }}>Date Range for Trending</Title>
          </div>
          <Text style={helperTextStyle}>
            Set the time window for tracking trending Activity.
          </Text> */}

          <div className="custom-date">
            {customStartDate &&
              customEndDate &&
              typeof customStartDate === "string" &&
              typeof customEndDate === "string" && (
                <>{renderDate(customStartDate, customEndDate)}</>
              )}

            <Button
              className="edit-btn"
              onClick={handleEdit}
            >
              Edit
            </Button>
          </div>

          {/* Clicks Threshold Section */}
          <div style={{ ...sectionStyle, marginTop: '24px' }}>
            <Title level={5} style={{ margin: 0 }}>Minimum Clicks to Trend</Title>
          </div>
          <Text style={helperTextStyle}>
            How many clicks a product needs to qualify as trending.
          </Text>

          <Input
            placeholder="Enter Number of Clicks"
            value={clickNumber}
            type="number"
            min={1}
            max={maxCount}
            onChange={(e: any) => {
              handleInputChange(e.target.value, setClickNumber);
            }}
            className="trending-input-box"
          />

          {component?.query && component?.query !== "" && (
            <Text style={{ fontSize: "14px", display: 'block', marginTop: '16px' }}>
              Content shown based on "{component?.query}"
            </Text>
          )}
          <label className="bold-label mt-4">Preview</label>
          <AddedTrendingProducts
            post_ids={component?.post_ids?.split(",")}
            username={username}
            user_id={user_id}
            setPostIds={setPostIds}
            handleRemovedPostIds={handleRemovedPostIds}
            componentName={name}
            refetchDashboard={refetchDashboard}
            removePostIds={editPostIds}
            setRemovePostIds={setEditPostIds}
            type={component?.type}
            displayName={component?.display_name}
            query={component?.query}
            title={component?.name}
            numberOfClicks={clickNumber}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            days={days}
            setIsChanged={setIsChanged}
            isChanged={isChanged}
            edit={edit}
            setEdit={setEdit}
            editIndex={editIndex}
            setEditIndex={setEditIndex}
            isActionLoading={isActionLoading}
            setIsActionLoading={setIsActionLoading}
            data={trendingData}
            isLoading={isLoading}
            refetch={refetch}
            name={name}
            setName={setName}
            errorMsg={errorMsg}
            setErrorMsg={setErrorMsg}
            shape={shape}
            isSoundOn={isSoundOn}
            component={component}
          />

          {/* Settings Section */}
          <label className="bold-label fontsize-12">Settings</label>
          <div className="carousel-controls-wrapper">
            <div className="addedContent">
              <CarouselControlWrapper
                showShape={true}
                showSound={true}
                showFilter={false}
                showOrder={false}
                shape={shape}
                setShape={setShape}
                isSoundOn={isSoundOn}
                setIsSoundOn={setIsSoundOn}
                type={type}
              />
            </div>
          </div>

          <RemovedTrendingProducts
            post_ids={component?.post_ids?.split(",")}
            username={username}
            user_id={user_id}
            setPostIds={setPostIds}
            handleRemovedPostIds={handleRemovedPostIds}
            componentName={name}
            refetchDashboard={refetchDashboard}
            removePostIds={editPostIds}
            setRemovePostIds={setEditPostIds}
            type={component?.type}
            displayName={component?.display_name}
            query={component?.query}
            title={component?.name}
            numberOfClicks={clickNumber}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            days={days}
            edit={edit}
            setEdit={setEdit}
            editIndex={editIndex}
            setEditIndex={setEditIndex}
            isActionLoading={isActionLoading}
            setIsActionLoading={setIsActionLoading}
            trendingData={removedTrendingData}
            isLoading={isLoading}
          />
        </div>
      </Modal>

      <Modal
        open={isEditDate}
        onOk={handleUpdateOk}
        onCancel={handleUpdateCancel}
        focusTriggerAfterClose={false}
        getContainer={false}
        okText="Update"
      >
        <CustomDateRange
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setIsCustomDateModalOpen={setIsCustomDateModalOpen}
          isCustomDateModalOpen={isCustomDateModalOpen}
          setSelectedDates={setSelectedDates}
          setCustomStartDate={setCustomStartDate}
          setCustomEndDate={setCustomEndDate}
          selectedDates={selectedDates}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          setLocalActiveTab={setLocalActiveTab}
          localActiveTab={localActiveTab}
          setDays={setDays}
        />
      </Modal>
    </>
  );
};

export default EditTrendingProducts;
